import { useEffect } from "react";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import ESGCalculator from "@/components/ESGCalculator";
import { Link } from "react-router-dom";
import { applySeo } from "@/lib/seo";

export default function CalculatorRendite() {
  useEffect(() => {
    applySeo({
      title: "Nachhaltigkeits-Rendite-Rechner – Grüne Geldanlage",
      description: "Interaktiver Rechner für nachhaltige ETF-Sparpläne: Endkapital, Wertzuwachs und Renditeprojektion – individuell einstellbar für Sparrate, Laufzeit und erwartete Rendite.",
      canonicalPath: "/rechner/nachhaltigkeits-rendite",
    });
  }, []);

  return (
    <div className="min-h-screen bg-brand-paper" data-testid="calc-rendite-page">
      <Header />
      <section className="max-w-4xl mx-auto px-6 lg:px-10 pt-20 pb-10">
        <div className="overline mb-4">Rechner</div>
        <h1 className="font-serif text-5xl md:text-6xl font-light leading-tight tracking-tight text-brand-dark mb-6">
          Nachhaltigkeits-Rendite-Rechner
        </h1>
        <p className="text-lg text-neutral-700 max-w-3xl mb-10">
          Wie entwickelt sich ein Sparplan in einen nachhaltigen ETF über die Jahre? Der Rechner zeigt
          Endkapital, eingezahlten Betrag und Wertzuwachs bei individuell einstellbarer Sparrate,
          Laufzeit und angenommener Nettorendite.
        </p>
      </section>

      <section className="max-w-6xl mx-auto px-6 lg:px-10 pb-20" data-testid="calc-rendite-tool">
        <ESGCalculator />
      </section>

      <section className="max-w-4xl mx-auto px-6 lg:px-10 pb-24">
        <div className="prose-editorial max-w-none">
          <h2>Wozu dient dieser Rechner?</h2>
          <p>
            Nachhaltige Geldanlagen werden in Deutschland zunehmend über regelmäßige Sparpläne
            umgesetzt – meist in Form eines ESG-konformen ETFs. Der Nachhaltigkeits-Rendite-Rechner
            beantwortet die zentrale Frage vor jedem Sparplan-Start: Welches Endkapital darf ich
            realistischerweise erwarten, wenn ich einen bestimmten Betrag monatlich in einen
            nachhaltigen ETF anspare? Statt nur Bauchgefühl liefert das Werkzeug eine
            nachvollziehbare Modellrechnung, die Sie in wenigen Sekunden an Ihre Situation
            anpassen können.
          </p>

          <h2>Wie funktioniert die Berechnung?</h2>
          <p>
            Grundlage ist die klassische Zinseszinsformel für Sparpläne mit monatlicher
            Einzahlung. Aus der eingegebenen Sparrate, der gewählten Laufzeit und der erwarteten
            Nettorendite pro Jahr wird jeder Monat einzeln verzinst. So entsteht eine
            realistischere Kurve als bei einer reinen Jahresrechnung – gerade bei
            langen Laufzeiten macht sich das im Ergebnis deutlich bemerkbar. Als Renditeannahme
            eignen sich für breit gestreute nachhaltige Aktien-ETFs historisch Werte zwischen
            vier und acht Prozent pro Jahr; für konservativere Anlegertypen sollten Sie eher am
            unteren Ende rechnen.
          </p>

          <h2>Welche Eingaben sind entscheidend?</h2>
          <p>
            Drei Größen bestimmen den Endwert eines Sparplans stärker als alle anderen: die
            monatliche Sparrate, die Anlagedauer und die durchschnittliche Nettorendite. Kleine
            Änderungen bei jeder dieser Größen verändern den Endwert überproportional stark –
            besonders bei langen Laufzeiten wirken sich schon 0,5 Prozentpunkte Renditeunterschied
            in fünfstellige Beträge um. Genau deshalb ist die Modellrechnung so wertvoll: Sie
            zwingt zur bewussten Auseinandersetzung mit realistischen Annahmen, statt sich auf
            Prospektwerbung zu verlassen.
          </p>

          <h2>Nachhaltige Rendite: Was historisch belegt ist</h2>
          <p>
            Zahlreiche Meta-Studien der vergangenen Jahre kommen zu einem konsistenten Bild:
            Nachhaltige Aktienportfolios liefern langfristig ähnliche Renditen wie ihre
            konventionellen Pendants – teils leicht darunter, teils leicht darüber, je nach
            Studienzeitraum und Definitionslogik. Die früher verbreitete Sorge, Nachhaltigkeit
            koste zwangsläufig Rendite, gilt in dieser Absolutheit als widerlegt. Wichtiger als
            der Nachhaltigkeitsaspekt selbst sind für die realisierte Rendite Faktoren wie
            Kostenquote (TER), Ausschüttungspolitik und die Diversifikation über Regionen und
            Sektoren.
          </p>

          <h2>Was der Rechner bewusst nicht kann</h2>
          <p>
            Modellrechnungen sind keine Prognosen. Sie liefern eine Orientierung, keine Garantie.
            In der Realität schwanken jährliche Aktienrenditen erheblich; einzelne Jahre können
            zweistellig ins Minus laufen, andere weit über dem Durchschnitt liegen. Der Rechner
            glättet diese Schwankungen zu einem hypothetischen Durchschnittspfad. Wer sich für
            eine detaillierte Renditeanalyse interessiert, findet in unseren Artikeln
            {" "}<Link to="/artikel/nachhaltige-etfs-vs-gruene-fonds">Nachhaltige ETFs vs. aktiv gemanagte grüne Fonds</Link> und
            {" "}<Link to="/artikel/esg-kriterien-einfach-erklaert">ESG-Kriterien einfach erklärt</Link>{" "}vertiefende Einordnungen.
          </p>

          <h2>Wichtig: Steuern und Vorabpauschale</h2>
          <p>
            Die Berechnung erfolgt vor Steuern. In Deutschland unterliegen realisierte Gewinne
            der Abgeltungsteuer plus Solidaritätszuschlag und ggf. Kirchensteuer; bei
            thesaurierenden Fonds fällt zusätzlich die Vorabpauschale an. Für Aktien-ETFs gilt
            eine Teilfreistellung von 30 Prozent, die die effektive Steuerlast reduziert. Der
            Rechner blendet diese Details bewusst aus, um die Wirkung der Grundparameter klar
            sichtbar zu machen. Für eine detaillierte Nachsteuerbetrachtung ist eine
            individuelle Beratung sinnvoll.
          </p>

          <h2>Zusammenfassung</h2>
          <p>
            Der Nachhaltigkeits-Rendite-Rechner ist Ihr Einstieg in eine faktenbasierte
            Sparplanplanung – nicht mehr, aber auch nicht weniger. Er zeigt Ihnen, welche
            Größenordnungen bei realistischen Annahmen entstehen, und macht die enorme Wirkung
            langer Laufzeiten erlebbar. Kombinieren Sie die Ergebnisse mit unseren beiden anderen
            Rechnern: dem <Link to="/rechner/co2-fussabdruck">CO₂-Fußabdruck-Portfolio-Rechner</Link>{" "}
            für die ökologische Perspektive und dem <Link to="/rechner/green-premium">Green Premium Rechner</Link>{" "}
            für den direkten Vergleich klassischer und nachhaltiger Varianten.
          </p>
          <p>
            Noch unsicher, welcher nachhaltige ETF überhaupt zu Ihnen passt? Unser{" "}
            <Link to="/rechner/nachhaltiger-etf-finder">ESG-Fit-Quiz</Link> filtert aus
            25 kuratierten ETFs die passende Empfehlung für Ihre Kriterien.
          </p>
        </div>
      </section>
      <Footer />
    </div>
  );
}
